'use client'

import * as React from 'react'
import { usePathname, useRouter } from 'next/navigation'
import algoliaAnalytics from 'search-insights'
import { createInsightsMiddleware } from 'instantsearch.js/es/middlewares'
import type { UiState } from 'instantsearch.js'
import { algoliasearch } from 'algoliasearch'
import { InstantSearch, useInstantSearch, Configure, useSortBy } from 'react-instantsearch'

import {
  discussionsAlgoliaAppId,
  discussionsAlgoliaApiKey,
  discussionsAlgoliaIndex,
  SORTING_OPTIONS,
} from './configs'
import type { QueryToSearchState } from './utils'
import { getSearchState, searchStateToQuery } from './utils'
import { useTopicFilter } from '../../hooks/use-topic-filter'

function DiscussionSortController() {
  useSortBy({ items: SORTING_OPTIONS })
  return null
}

function InsightsMiddleware() {
  const { addMiddlewares } = useInstantSearch()
  React.useEffect(() => {
    const middleware = createInsightsMiddleware({ insightsClient: algoliaAnalytics })
    return addMiddlewares(middleware)
  }, [addMiddlewares])
  return null
}

export const discussionsSearchClient = algoliasearch(
  discussionsAlgoliaAppId,
  discussionsAlgoliaApiKey,
  {
    baseHeaders:
      typeof window === 'undefined' ? { referer: 'https://www.creativefabrica.com' } : {},
  },
)

function buildTopicFacetFilters(selected: string | null): string[][] | undefined {
  return selected ? [[`topic_slug:${selected}`]] : undefined
}

export const SearchDiscussionsProvider = ({
  children,
  slug,
  isDiscussion = false,
}: {
  children: React.ReactNode
  slug?: string
  isDiscussion?: boolean
}) => {
  const { replace } = useRouter()
  const pathname = usePathname()
  const { selected: selectedTopics } = useTopicFilter()

  // Compute initial UI state from the native URL on the client.
  const [initialUiState, setInitialUiState] = React.useState<UiState | null>(null)
  React.useEffect(() => {
    const qs = new URLSearchParams(window.location.search).toString()
    const { uiState } = getSearchState({ url: qs })
    setInitialUiState(uiState)
  }, [])

  const handleStateChange = React.useCallback(
    ({
      uiState,
      setUiState,
    }: {
      uiState: UiState
      setUiState: (ui: UiState | ((prev: UiState) => UiState)) => void
    }) => {
      const newQuery = searchStateToQuery({ uiState } as QueryToSearchState)

      const curr = new URLSearchParams(window.location.search)
      const next = new URLSearchParams(newQuery)
      const topics = curr.getAll('topic_slug')
      next.delete('topic_slug')
      topics.forEach(t => next.append('topic_slug', t))

      const merged = next.toString()
      const newState = getSearchState({ url: merged })
      setUiState(newState.uiState)
      replace(merged ? `${pathname}?${merged}` : pathname, { scroll: false })
    },
    [replace, pathname],
  )

  if (!initialUiState) {
    return isDiscussion ? null : children
  }

  return (
    <InstantSearch
      initialUiState={initialUiState}
      onStateChange={handleStateChange}
      searchClient={discussionsSearchClient}
      indexName={discussionsAlgoliaIndex}
    >
      <Configure
        hitsPerPage={25}
        filters={slug ? `clubhouse_name:"${slug}"` : ''}
        facetFilters={buildTopicFacetFilters(selectedTopics)}
      />
      {children}
      <DiscussionSortController />
      <InsightsMiddleware />
    </InstantSearch>
  )
}
